import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mic2, Calendar, User, BookOpen, ChevronRight, Star } from 'lucide-react';
import { useMosque } from '../contexts/MosqueContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
};

const formatDateShort = (d) => {
  if (!d) return '-';
  return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const ImamKhatibPage = () => {
  const { mosqueId } = useMosque();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mosqueId) {
      axios.get(`${API_URL}/api/imam-khatib`, { params: { mosque_id: mosqueId, limit: 30 } })
        .then(r => setList(r.data || []))
        .catch(() => setList([]))
        .finally(() => setLoading(false));
    }
  }, [mosqueId]);

  const today = new Date().toISOString().split('T')[0];
  // Sort: upcoming first (asc), then past (desc)
  const upcoming = list.filter(i => i.tanggal >= today).sort((a, b) => a.tanggal.localeCompare(b.tanggal));
  const past = list.filter(i => i.tanggal < today).sort((a, b) => b.tanggal.localeCompare(a.tanggal));

  const nextJumat = upcoming[0] || null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#F8F9FA] pb-24"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700 px-6 pt-16 pb-12 text-white relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-12 -right-12 w-52 h-52 rounded-full bg-white/5" />
        <div className="absolute top-12 -right-6 w-28 h-28 rounded-full bg-amber-400/10" />

        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="w-14 h-14 rounded-2xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center mb-4">
            <Mic2 className="w-7 h-7 text-amber-300" />
          </div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Jadwal Jumat</h1>
          <p className="text-white/70 text-sm font-body">Imam & Khatib Sholat Jumat</p>
        </motion.div>

        {/* Next Jumat Featured Card */}
        {nextJumat && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-5"
            data-testid="next-jumat-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-amber-300" fill="currentColor" />
              <span className="text-xs font-body font-semibold text-amber-300 uppercase tracking-wide">
                Jumat Mendatang
              </span>
            </div>
            <p className="text-white/80 text-xs font-body mb-3">{formatDate(nextJumat.tanggal)}</p>

            <div className="space-y-2.5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-white/80" />
                </div>
                <div>
                  <p className="text-[10px] text-white/50 font-body uppercase tracking-wider">Imam</p>
                  <p className="text-white font-body font-semibold text-sm">{nextJumat.imam}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mic2 className="w-4 h-4 text-amber-300" />
                </div>
                <div>
                  <p className="text-[10px] text-white/50 font-body uppercase tracking-wider">Khatib</p>
                  <p className="text-white font-body font-semibold text-sm">{nextJumat.khatib}</p>
                </div>
              </div>
              {nextJumat.tema && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BookOpen className="w-4 h-4 text-white/80" />
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 font-body uppercase tracking-wider">Tema Khutbah</p>
                    <p className="text-white font-body text-sm italic">"{nextJumat.tema}"</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-200 border-t-emerald-700" />
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-20">
            <Mic2 className="w-16 h-16 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-500 font-body text-lg">Belum ada jadwal</p>
            <p className="text-stone-400 text-sm mt-1">
              Admin masjid dapat menambahkan jadwal melalui dasbor
            </p>
          </div>
        ) : (
          <>
            {/* Upcoming (excluding the first one already shown above) */}
            {upcoming.length > 1 && (
              <div className="mb-6">
                <h3 className="text-sm font-body font-semibold text-stone-500 uppercase tracking-wider mb-3">
                  Jadwal Mendatang
                </h3>
                <div className="space-y-3">
                  {upcoming.slice(1).map((item, i) => (
                    <JadwalListItem key={item.id} item={item} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div>
                <h3 className="text-sm font-body font-semibold text-stone-500 uppercase tracking-wider mb-3">
                  Riwayat Jadwal
                </h3>
                <div className="space-y-3">
                  {past.map((item, i) => (
                    <JadwalListItem key={item.id} item={item} index={i} isPast />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

const JadwalListItem = ({ item, index, isPast }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.05 * index }}
    className={`bg-white rounded-2xl border p-4 ${isPast ? 'border-stone-100 opacity-75' : 'border-emerald-100 shadow-sm'}`}
    data-testid={`imam-khatib-list-${item.id}`}
  >
    <div className="flex items-start gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isPast ? 'bg-stone-100' : 'bg-emerald-50'}`}>
        <Calendar className={`w-5 h-5 ${isPast ? 'text-stone-400' : 'text-emerald-600'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium mb-1.5 ${isPast ? 'text-stone-400' : 'text-emerald-700'}`}>
          {formatDateShort(item.tanggal)}
        </p>
        <div className="space-y-1">
          <p className="text-sm font-body text-stone-700">
            <span className="font-semibold text-stone-800">Imam:</span> {item.imam}
          </p>
          <p className="text-sm font-body text-stone-700">
            <span className="font-semibold text-stone-800">Khatib:</span> {item.khatib}
          </p>
          {item.tema && (
            <p className="text-xs text-stone-500 italic mt-1">"{item.tema}"</p>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

export default ImamKhatibPage;
